import { ActivatedRouteSnapshot, Route } from '@angular/router'

/**
 * Which pages stand alone, without the platform's bar, breadcrumbs and prompt cards (Release 5,
 * 31/10/2026, PUBLIC-02). The routes say it themselves with `data: { public: ... }`:
 * - true: always (watch, files, offer, unlock, n, unsubscribe, join, welcome) -- each has its own
 *   header, and signed in you used to get two;
 * - 'signed-out': only for a visitor (the live pages). A signed-in member keeps the top bar and
 *   the breadcrumbs, so there is a way back (CC, 10/10/2026).
 */
export type PublicKind = true | 'signed-out'

function kindOf (data: unknown): PublicKind | null {
  const v = (data as { public?: unknown } | undefined)?.public
  return v === true || v === 'signed-out' ? v : null
}

/** The flag on the page now open: any route on the way down may carry it. */
export function publicKindOfSnapshot (root: ActivatedRouteSnapshot | null | undefined): PublicKind | null {
  let kind: PublicKind | null = null
  for (let r = root; r; r = r.firstChild) kind = kindOf(r.data) ?? kind
  return kind
}

/**
 * The same answer from an address, before the router has finished its first navigation (the
 * shell decides at start-up whether to ask for the terms of service). Only top-level routes carry
 * the flag, so their paths are matched segment by segment, ':param' matching anything.
 */
export function publicKindOfPath (path: string, routes: readonly Route[]): PublicKind | null {
  const segs = path.split(/[?#]/)[0].split('/').filter(Boolean)
  for (const r of routes) {
    const kind = kindOf(r.data)
    if (!kind || r.path === undefined) continue
    const parts = r.path.split('/').filter(Boolean)
    if (parts.length === segs.length && parts.every((p, i) => p.startsWith(':') || p === segs[i])) return kind
  }
  return null
}

export function isPublicPage (kind: PublicKind | null, signedIn: boolean): boolean {
  return kind === true || (kind === 'signed-out' && !signedIn)
}
